// Define a custom element for the cart remove button
class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    // Add a click event listener to the button
    this.addEventListener('click', (event) => {
      event.preventDefault();
      // Find the nearest cart items element and update the quantity to 0
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

// Define the custom element 'cart-remove-button'
customElements.define('cart-remove-button', CartRemoveButton);

// Define a custom element for the cart items
class CartItems extends HTMLElement {
  constructor() {
    super();
    // Find the line item status element
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    // Debounce the onChange function to handle input changes
    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    // Add a change event listener to the cart items
    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  // Initialize a variable to unsubscribe from cart updates
  cartUpdateUnsubscriber = undefined;

  // Called when the element is added to the DOM
  connectedCallback() {
    // Subscribe to cart update events
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  // Called when the element is removed from the DOM
  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      // Unsubscribe from cart updates
      this.cartUpdateUnsubscriber();
    }
  }

  // Handle input change events
  onChange(event) {
    // Update the quantity of the cart item
    this.updateQuantity(
      event.target.dataset.index, // Index of the cart item
      event.target.value, // New quantity
      document.activeElement.getAttribute('name'), // Name of the active element
      event.target.dataset.quantityVariantId // Variant ID of the quantity
    );
  }

    // Handle cart update events
  onCartUpdate() {
    // Check if the element is a cart drawer or main cart items
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      // Destroy BOGOS instance before DOM replacement to prevent stale references
      if (window.cartBogosUpsellsInstance && typeof window.cartBogosUpsellsInstance.destroy === 'function') {
        try {
          window.cartBogosUpsellsInstance.destroy();
          window.cartBogosUpsellsInstance = null;
        } catch (e) {
          console.warn('[CartItems] Error destroying BOGOS instance:', e);
        }
      }

      // Fetch the updated cart drawer content
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          // Parse the HTML response
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          // Define selectors to identify elements in the fetched content
          const selectors = ['cart-drawer-items', '.cart-bogos-upsells-wrapper', '.cart-drawer__footer'];
          
          // Check if cart is empty from fetched HTML
          const cartDrawerItems = html.querySelector('cart-drawer-items');
          const isEmpty = cartDrawerItems && cartDrawerItems.classList.contains('is-empty');
          
          // Hide BOGOS wrapper if cart is empty
          if (isEmpty) {
            const wrapper = document.querySelector(".cart-bogos-upsells-wrapper");
            if (wrapper) {
              wrapper.style.display = "none";
              wrapper.style.visibility = "hidden";
              wrapper.style.height = "0";
              wrapper.style.overflow = "hidden";
              wrapper.style.padding = "0";
              wrapper.style.margin = "0";
            }
          }
          
          // Track cart items before replacement for animation
          const cartItemsBefore = document.querySelectorAll('.cart-drawer .cart-item');
          const itemIdsBefore = new Set();
          cartItemsBefore.forEach((item) => {
            const id = item.id || item.getAttribute('data-cart-item-key');
            if (id) itemIdsBefore.add(id);
          });

          // Iterate through selectors to find and replace corresponding elements
          for (const selector of selectors) {
            // Find the target element in the current DOM
            const targetElement = document.querySelector(selector);
            // Find the corresponding source element in the fetched content
            const sourceElement = html.querySelector(selector);
            // If both target and source elements exist, replace the target with the source
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }

          // Trigger animation for new items after DOM update
          requestAnimationFrame(() => {
            setTimeout(() => {
              if (window.cartDrawerInitManager && typeof window.cartDrawerInitManager.animateNewCartItems === 'function') {
                window.cartDrawerInitManager.animateNewCartItems();
              }
            }, 50);
          });

          // Ensure upsells wrapper exists after empty -> non-empty transition
          const sourceUpsells = html.querySelector('.cart-bogos-upsells-wrapper');
          if (sourceUpsells && !document.querySelector('.cart-bogos-upsells-wrapper')) {
            const footerEl = document.querySelector('.drawer__footer');
            if (footerEl && footerEl.parentNode) {
              footerEl.parentNode.insertBefore(sourceUpsells, footerEl);
            }
          }
          
          // Set is-empty class on cart-drawer element after DOM replacement
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            if (isEmpty) {
              cartDrawer.classList.add('is-empty');
            } else {
              cartDrawer.classList.remove('is-empty');
            }
          }
          
          // Publish cart update event
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-items',
            isEmpty: isEmpty,
            fromDrawerUpdate: true
          });

          // Reinitialize components after DOM update
          if (window.cartDrawerInitManager) {
            setTimeout(() => {
              window.cartDrawerInitManager.scheduleReinit();
            }, 50);
          }
        })
        .catch((e) => {
          // Log any errors that occur during fetching and updating
          console.error(e);
        });
    } else {
      // Fetch the updated main cart items content
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          // Parse the HTML response
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          // Find the 'cart-items' element in the fetched content
          const sourceQty = html.querySelector('cart-items');
          // Update the inner HTML of the current element with the fetched 'cart-items' content
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          // Log any errors that occur during fetching and updating
          console.error(e);
        });
    }
  }

  // Get the sections to render
  getSectionsToRender() {
    return [
      // Define sections to render with their IDs, section IDs, and selectors
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  // Update the quantity of a cart item
  updateQuantity(line, quantity, name, variantId) {
    // Enable loading state for the cart item
    this.enableLoading(line);

    // Create a JSON body for the request
    const body = JSON.stringify({
      line, // Line item number
      quantity, // New quantity
      sections: this.getSectionsToRender().map((section) => section.section), // Sections to render
      sections_url: window.location.pathname, // URL of the sections
    });

    // Send a request to update the cart item quantity
    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        // Parse the response state
        const parsedState = JSON.parse(state);
        // Get the quantity element for the cart item
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        // Get all cart items
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          // Handle errors
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        // Toggle the 'is-empty' class based on item count
        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        // Replace sections with updated content
        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        // Focus management
        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

          // Publish cart update event
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId, itemCount: parsedState.item_count, isEmpty: parsedState.item_count === 0 });
      })
      .catch(() => {
        // Handle errors
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        // Disable loading state for the cart item
        this.disableLoading(line);
      });
  }

  // Update the live regions with error messages
  updateLiveRegions(line, message) {
    // Update the line item error message
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    // Hide the line item status element
    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    // Update the cart status live region
    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    // Hide the cart status live region after a delay
    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  // Get the inner HTML content of a section
  getSectionInnerHTML(html, selector) {
    // Parse the HTML and return the inner HTML content of the specified selector
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  // Enable loading state for a specific cart item
  enableLoading(line) {
    // Add a class to disable interaction with the cart items
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    // Show loading spinners for the cart item
    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    // Remove the 'hidden' class to show loading spinners
    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    // Blur the active element and update accessibility attributes
    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  // Disable loading state for a specific cart item
  disableLoading(line) {
    // Remove the class that disables interaction with the cart items
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    // Hide loading spinners for the cart item
    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    // Add the 'hidden' class to hide loading spinners
    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

// Define the custom element 'cart-items'
customElements.define('cart-items', CartItems);

// Define a custom element for the cart clear button
class CartClearButton extends HTMLElement {
  constructor() {
    super();
    // Debounce the onChange function to handle input changes
    const debouncedOnClick = debounce((event) => {
      this.onClick(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    // Add a change event listener to the cart items
    this.addEventListener('click', debouncedOnClick.bind(this));
  }

  // Called when the element is added to the DOM
  connectedCallback() {

  }

  // Called when the element is removed from the DOM
  disconnectedCallback() {

  }

  // Get the sections to render
  getSectionsToRender() {
    return [
      // Define sections to render with their IDs, section IDs, and selectors
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  // Get the inner HTML content of a section
  getSectionInnerHTML(html, selector) {
    // Parse the HTML and return the inner HTML content of the specified selector
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  onClick(event){
    console.log("cart-clear-button click", this);
    // Create a JSON body for the request
    const body = JSON.stringify({
      sections: this.getSectionsToRender().map((section) => section.section), // Sections to render
      sections_url: window.location.pathname, // URL of the sections
    });

    // Send a request to clear the cart, and then update all cart related DOM renders.
    fetch(`${routes.cart_clear_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        // Parse the response state
        const parsedState = JSON.parse(state);

        if (parsedState.errors) {
          return;
        }

        // Toggle the 'is-empty' class based on item count
        const cartItems = document.querySelector('cart-items');
        const cartDrawerItems = document.querySelector('cart-drawer-items');
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartItems) cartItems.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerItems) cartDrawerItems.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        // Replace sections with updated content
        this.getSectionsToRender().forEach((section) => {
          console.log(`section`, section);
          const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
          // if we can locate the element we need to replace, proceed.
          if(elementToReplace){
            console.log(`Found section for section: `, section);
            elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
          } else {
            console.log(`Unable to find section for section: `, section);
          }
        });

        // Publish cart update event
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-clear-button', cartData: parsedState, itemCount: parsedState.item_count, isEmpty: parsedState.item_count === 0 });
      })
      .catch(() => {
        // Handle errors
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {

      });
  }
}

// Define the custom element 'cart-clear-button'
customElements.define('cart-clear-button', CartClearButton);

// Define a custom element for the cart note
if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        // Add an input event listener to debounce note changes
        this.addEventListener(
          'input',
          debounce((event) => {
            // Create a JSON body for the request and send a request to update the cart note
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
